// Snapshot (formerly Dashboard 3; a built-in tab beside Classic and Overview, see DashboardWidgets.js): net P&L with the
// last 30 trading days as a line, four number tiles and today; one compact card per account; daily
// P&L bars beside the biggest leak; the P&L calendar; and the best, worst and untagged trades as a
// table.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import Calendar from './Calendar';
import { accountStatus } from './balance';
import { dayNets } from './dashboardMetrics';
import { isPropAccount } from './DashboardOverview';
import { addRoutineRule } from './routineRules';
import { ScoreBar } from './ScoreViews';
import { netPnl, tradingDayKey, usd } from './pnl';
import { performanceStats, topInsight, unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const money = (n) => `${n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const short = (n) => {
  const a = Math.abs(n);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}$${a >= 1000 ? `${(a / 1000).toFixed(1)}K` : Math.round(a)}`;
};
const dayShort = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
// Four evenly spaced date labels under a chart of these days.
const axisKeys = (keys) => (keys.length < 2 ? keys : [0, 1, 2, 3].map((i) => keys[Math.round((i / 3) * (keys.length - 1))]));

// CME futures hours in Chicago: Sunday 5 PM to Friday 4 PM, with a break from 4 to 5 PM each day.
function marketState() {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'short', hour: 'numeric', hourCycle: 'h23' })
    .formatToParts(new Date()).map((p) => [p.type, p.value]));
  const day = parts.weekday;
  const hour = Number(parts.hour);
  if (day === 'Sat' || (day === 'Sun' && hour < 17) || (day === 'Fri' && hour >= 16)) return 'weekend';
  if (hour === 16) return 'break';
  return 'open';
}

// Net P&L for the period, with the running P&L of the last 30 trading days.
function Hero({ trades, accounts, period }) {
  const { t } = useI18n();
  const net = trades.reduce((s, x) => s + netPnl(x), 0);
  const accountCount = new Set(trades.map((x) => String(x.accountId))).size || accounts.length;
  const days = dayNets(trades).slice(-30);
  let run = 0;
  const points = days.map((d) => ({ key: d.key, v: (run += d.net) }));
  const W = 600;
  const H = 170;
  const lo = Math.min(0, ...points.map((p) => p.v));
  const hi = Math.max(0, ...points.map((p) => p.v));
  const x = (i) => (i / Math.max(1, points.length - 1)) * W;
  const y = (v) => 10 + (1 - (v - lo) / Math.max(1, hi - lo)) * (H - 20);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <section className="dov-card d3-hero">
      <div className="d3-hero-head">
        <div>
          <span className="dov-label">{t('Net P&L · {period} · after fees', { period: period })}</span>
          <strong className={`d3-hero-net ${tone(net)}`}>{money(net)}</strong>
          <span className="dov-muted">{t(accountCount === 1 ? '{n} trades in 1 account' : '{n} trades across {a} accounts', { n: trades.length, a: accountCount })}</span>
        </div>
        {points.length > 0 && (
          <div className="d3-hero-window">
            <span className="dov-muted">{t('Last 30 trading days')}</span>
            <strong className={tone(last.v)}>{signed(last.v)}</strong>
            <small className="dov-muted">{dayShort(points[0].key)} – {dayShort(last.key)}</small>
          </div>
        )}
      </div>
      {points.length > 1 ? (
        <div className="d3-plot">
          <div className="d3-plot-area">
          <span className="d3-zero-label" style={{ top: `${(y(0) / H) * 100}%` }}>$0</span>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t('Cumulative P&L, last 30 trading days')}>
            <defs><linearGradient id="d3-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#1fd7ef" stopOpacity="0.32" /><stop offset="1" stopColor="#1fd7ef" stopOpacity="0.04" /></linearGradient></defs>
            <line x1="0" x2={W} y1={y(0)} y2={y(0)} className="zero" />
            <path d={`${line} L${W} ${y(0)} L0 ${y(0)} Z`} fill="url(#d3-fill)" />
            <path d={line} className="line" />
          </svg>
          <span className={`d3-end ${last.v < 0 ? 'loss' : 'gain'}`} style={{ top: `${(y(last.v) / H) * 100}%` }} />
          </div>
          <span className="d3-axis">{axisKeys(points.map((p) => p.key)).map((k, i) => <small key={`${k}${i}`}>{dayShort(k)}</small>)}</span>
        </div>
      ) : <p className="dov-muted">{t('The chart fills in after a few trading days.')}</p>}
    </section>
  );
}

// Win rate, profit factor, average win and loss, then today and this week.
function Tiles({ trades }) {
  const { t } = useI18n();
  const s = performanceStats(trades);
  const wins = trades.filter((x) => netPnl(x) > 0).length;
  const losses = trades.filter((x) => netPnl(x) < 0).length;
  const pf = s.profitFactor;
  const ratio = s.avgWin && s.avgLoss ? s.avgWin / s.avgLoss : null;
  const todayKey = tradingDayKey(new Date());
  const today = trades.filter((x) => tradingDayKey(x.date) === todayKey);
  const monday = new Date(`${todayKey}T12:00:00`);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const weekKey = monday.toISOString().slice(0, 10);
  const week = trades.filter((x) => tradingDayKey(x.date) >= weekKey);
  const weekNet = week.reduce((sum, x) => sum + netPnl(x), 0);
  const market = marketState();
  return (
    <div className="d3-tiles">
      <div className="dov-card d3-tile">
        <span className="dov-label">{t('Win rate')}</span>
        <strong className="d3-num">{s.winRate === null ? '—' : `${Math.round(s.winRate)}%`}</strong>
        <span className="d3-split"><b style={{ width: `${s.winRate || 0}%` }} /><i /></span>
        <small className="dov-muted">{t('{w} wins · {l} losses', { w: wins, l: losses })}</small>
      </div>
      <div className="dov-card d3-tile">
        <span className="dov-label">{t('Profit factor')}</span>
        <strong className={`d3-num ${pf === null ? '' : pf >= 1 ? 'gain' : 'loss'}`}>{pf === null ? '—' : pf === Infinity ? '∞' : pf.toFixed(2)}</strong>
        <span className="d3-pf"><b className={pf >= 1 ? 'gain' : 'loss'} style={{ width: `${Math.min(1, (pf === Infinity ? 2 : pf || 0) / 2) * 100}%` }} /><i /></span>
        <small className="dov-muted">{pf === null ? t('Needs a win and a loss') : pf < 1 ? t('Needs 1.00 to break even') : t('Above the 1.00 break-even line')}</small>
      </div>
      <div className="dov-card d3-tile">
        <span className="dov-label">{t('Average win')}</span>
        <strong className="d3-num gain">{s.avgWin === null ? '—' : signed(s.avgWin)}</strong>
        <small className="dov-muted">{ratio === null ? t('Needs a win and a loss') : t('{x}× your average loss', { x: ratio.toFixed(1) })}</small>
      </div>
      <div className="dov-card d3-tile">
        <span className="dov-label">{t('Average loss')}</span>
        <strong className="d3-num loss">{s.avgLoss === null ? '—' : `−${usd.format(s.avgLoss)}`}</strong>
        <small className="dov-muted">{t('Per losing trade')}</small>
      </div>
      <div className="dov-card d3-today">
        <span>
          <span className="dov-label">{t('Today')}</span>
          {today.length ? (
            <b className={tone(s.today)}>{signed(s.today)} · {t(today.length === 1 ? '{n} trade' : '{n} trades', { n: today.length })}</b>
          ) : (
            <b>{market === 'weekend' ? t('Markets closed · reopen Sun 5:00 PM CT') : market === 'break' ? t('Markets closed · reopen 5:00 PM CT') : t('Markets open · no trades yet')}</b>
          )}
        </span>
        <span className="d3-today-week">
          <span className="dov-label">{t('This week')}</span>
          <b className={tone(weekNet)}>{week.length ? signed(weekNet) : t('No trades')}</b>
        </span>
      </div>
    </div>
  );
}

// One account, compact: balance (or open positions), what's left to pass and the max-loss room for
// prop accounts, and the P&L.
function AccountTile({ account, trades, positions, onOpen }) {
  const { t } = useI18n();
  const mine = trades.filter((x) => String(x.accountId) === String(account._id));
  const status = account.size ? accountStatus(account, mine) : null;
  const days = dayNets(mine);
  const last = days.length ? days[days.length - 1] : null;
  const prop = isPropAccount(account);
  const watch = status && (status.risk === 'danger' || status.risk === 'warning' || status.consistencyOk === false);
  const kind = watch ? 'watch' : prop ? 'prop' : 'personal';
  const unrealized = positions.reduce((s, p) => s + (Number(p.unrealized) || 0), 0);
  const pnl = positions.length && !status ? unrealized : status && prop ? status.balance - account.size : last ? last.net : 0;
  return (
    <button type="button" className={`dov-card d3-acct${watch ? ' watch' : ''}`} onClick={onOpen}>
      <span className="dov-acct-head">
        <span className={`dov-dot ${kind}`} aria-hidden="true" />
        <strong>{account.name}</strong>
        <span className={`dov-tag ${kind}`}>{t(watch ? 'Watch' : prop ? 'Prop' : 'Personal')}</span>
      </span>
      {positions.length > 0 && !status ? (
        <>
          <b className="d3-acct-balance">{t(positions.length === 1 ? '{n} open position' : '{n} open positions', { n: positions.length })}</b>
          <small className="dov-muted d3-acct-line">{positions.slice(0, 2).map((p) => `${p.symbol} ${t(p.side).toLowerCase()} ${p.size} @ ${p.entryPrice}`).join(' · ')} · P&L <b className={tone(pnl)}>{signed(pnl)}</b></small>
        </>
      ) : (
        <>
          <b className="d3-acct-balance">{status ? money(status.balance) : signed(days.reduce((s, d) => s + d.net, 0))}</b>
          {status && prop && status.target ? (
            <small className="dov-muted d3-acct-line d3-wrap">
              {t('To pass')} <b>{usd.format(Math.max(0, status.target - status.balance))}</b>
              {status.toBlow !== undefined && <><br />{t('Max loss in')} <b className="gold">{usd.format(Math.max(0, status.toBlow))}</b></>}
            </small>
          ) : null}
          <small className="dov-muted d3-acct-line">P&L <b className={tone(pnl)}>{signed(pnl)}</b></small>
        </>
      )}
    </button>
  );
}

// Daily P&L as bars for the last 30 trading days.
function DailyChart({ trades }) {
  const { t } = useI18n();
  const days = dayNets(trades).slice(-30);
  const green = days.filter((d) => d.net > 0).length;
  const red = days.filter((d) => d.net < 0).length;
  const top = Math.max(1, ...days.map((d) => Math.abs(d.net)));
  const slot = 100 / Math.max(1, days.length);
  return (
    <section className="dov-card d3-daily">
      <div className="dov-row">
        <strong className="dov-h">{t('Daily P&L · last 30 trading days')}</strong>
        {days.length > 0 && <small><span className="gain">{t('{n} green', { n: green })}</span> · <span className="loss">{t('{n} red', { n: red })}</span></small>}
      </div>
      {days.length ? (
        <div className="d3-bars">
          <span className="d3-y"><small>{short(top)}</small><small>$0</small><small>{short(-top)}</small></span>
          <div className="d3-bars-plot">
            <span className="d3-bars-zero" />
            {days.map((d, i) => (
              <span key={d.key} className="d3-bar-slot" style={{ left: `${i * slot}%`, width: `${slot}%` }} title={`${dayShort(d.key)}: ${signed(d.net)}`}>
                <i className={tone(d.net)} style={{ height: `${(Math.abs(d.net) / top) * 50}%`, [d.net >= 0 ? 'bottom' : 'top']: '50%' }} />
              </span>
            ))}
          </div>
          <span className="d3-axis d3-bars-axis">{axisKeys(days.map((d) => d.key)).map((k, i) => <small key={`${k}${i}`}>{dayShort(k)}</small>)}</span>
        </div>
      ) : <p className="dov-muted">{t('No trades yet')}</p>}
    </section>
  );
}

// The biggest leak: the finding with its percentage picked out, what it cost, the comparison, the
// recommendation and a rule to add.
export function LeakCard({ trades, library }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const insight = useMemo(() => topInsight(trades, library, true), [trades, library]);
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const real = insight && insight.kind !== 'none';
  async function add() {
    setBusy(true);
    try {
      await addRoutineRule(insight.rule);
      setAdded(true);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className={`dov-card d3-leak${real ? '' : ' quiet'}`}>
      <span className="d3-leak-head">
        <span className="d3-leak-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16"><path d="M2 3l4.5 5 3-3L14 11" /><path d="M10 11h4V7" /></svg>
        </span>
        <span className="dov-label">{t(real ? 'Biggest leak right now' : 'Insight')}</span>
      </span>
      {!insight ? <p className="d3-leak-text">{t('Log at least 10 trades in the last 30 days to unlock your first insight.')}</p> : (
        <>
          <p className="d3-leak-text">{insight.text.split(/(\d+%)/).map((part, i) => (i % 2 ? <span key={i} className="gold">{part}</span> : part))}</p>
          {real && (
            <div className="d3-leak-boxes">
              {insight.cost !== undefined && (
                <span><small className="dov-muted">{t('Cost')}</small><b className={tone(insight.cost)}>{signed(insight.cost)}</b><small className="dov-muted">{t('over {n} trades', { n: insight.sample })}</small></span>
              )}
              {insight.compare ? (
                <span><small className="dov-muted">{t('Win rate otherwise')}</small><b>{insight.compare.other}%</b><small className="dov-muted">{t('all other trades')}</small></span>
              ) : (
                <span><small className="dov-muted">{t('Based on')}</small><b>{insight.sample}</b><small className="dov-muted">{t('trades in the last 30 days')}</small></span>
              )}
            </div>
          )}
          {insight.tip && <p className="d3-leak-tip">{insight.tip}</p>}
          <div className="dov-row d3-leak-actions">
            {real && insight.rule && (added
              ? <span className="gain">✓ {t('Added to your rules')}</span>
              : <button type="button" className="dov-gold small" disabled={busy} onClick={add} title={t('Adds “{rule}” to your rules in Morning prep.', { rule: insight.rule })}>{t('Add as a rule')}</button>)}
            <button type="button" className="link-button" onClick={() => navigate('/app/insights')}>{t('See all insights')} &rarr;</button>
          </div>
        </>
      )}
    </section>
  );
}

// Best, worst and untagged trades as a table; Open opens the trade.
export function TradeTable({ trades, onEditTrade }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState('best');
  const byNet = [...trades].sort((a, b) => netPnl(b) - netPnl(a));
  const waiting = unjournaledTrades(trades);
  const rows = {
    best: byNet.filter((x) => netPnl(x) > 0).slice(0, 5),
    worst: byNet.filter((x) => netPnl(x) < 0).reverse().slice(0, 5),
    tags: waiting.slice(0, 5),
  }[tab];
  const when = (x) => new Date(x.date).toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <section className="dov-card d3-table">
      <div className="dov-row">
        <div className="dov-seg" role="group" aria-label={t('Show')}>
          {[['best', 'Best trades'], ['worst', 'Worst trades'], ['tags', 'Needs tags']].map(([id, label]) => (
            <button key={id} type="button" className={tab === id ? 'on' : ''} aria-pressed={tab === id} onClick={() => setTab(id)}>
              {t(label)}{id === 'tags' && waiting.length > 0 && ` · ${waiting.length}`}
            </button>
          ))}
        </div>
        <button type="button" className="link-button" onClick={() => navigate('/app')}>{t('All trades')} &rarr;</button>
      </div>
      {rows.length ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t('Contract')}</th><th>{t('Side')}</th><th>{t('When')}</th><th className="num">{t('P&L')}</th><th aria-label={t('Open')} /></tr></thead>
            <tbody>
              {rows.map((x) => {
                const side = String(x.side || '').toLowerCase().startsWith('s') ? 'short' : 'long';
                return (
                  <tr key={x._id}>
                    <td><b>{x.symbol}</b></td>
                    <td><span className={`dov-side ${side}`}>{t(side === 'short' ? 'Short' : 'Long')}</span> <span className="dov-muted">× {x.size}</span></td>
                    <td className="dov-muted">{when(x)}</td>
                    <td className={`num ${tone(netPnl(x))}`}><b>{signed(netPnl(x))}</b></td>
                    <td className="num"><button type="button" className="link-button" onClick={() => onEditTrade && onEditTrade(x)}>{t('Open')}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="dov-muted">{t(tab === 'best' ? 'No winning trades yet.' : tab === 'worst' ? 'No losing trades yet.' : 'Every trade has tags or notes. Nice work.')}</p>}
    </section>
  );
}

export default function DashboardThree({ ctx }) {
  const { trades, accounts, account, shownIds, allTrades, onSelectAccount, scopeLine } = ctx;
  const shown = accounts.filter((a) => !a.archived && (!shownIds || shownIds.includes(a._id)));
  const latestDay = trades.length ? trades.map((x) => tradingDayKey(x.date)).sort().pop() : null;
  // scopeLine is "140 trades · All time · …": the period is its second part.
  const period = String(scopeLine || '').split(' · ')[1] || '';
  // Open positions from Interactive Brokers sync, shown on their account's card.
  const [positions, setPositions] = useState([]);
  useEffect(() => {
    let live = true;
    api('/ibkr/positions').then((d) => live && setPositions((d && d.positions) || [])).catch(() => {});
    return () => { live = false; };
  }, []);
  return (
    <div className="dov d3">
      <div className="d3-top">
        <Hero trades={trades} accounts={shown} period={period} />
        <Tiles trades={trades} />
      </div>
      <ScoreBar trades={trades} />
      {shown.length > 0 && (
        <div className="d3-accounts">
          {shown.map((a) => <AccountTile key={a._id} account={a} trades={allTrades} positions={positions.filter((p) => String(p.accountId) === String(a._id))} onOpen={() => onSelectAccount(a._id)} />)}
        </div>
      )}
      <div className="d3-mid">
        <DailyChart trades={trades} />
        <LeakCard trades={trades} library={ctx.library} />
      </div>
      <section className="dov-card dov-cal">
        <Calendar trades={trades} account={account} size={ctx.size} startDay={latestDay} insights={ctx.insights} library={ctx.library} notes={ctx.notes} setNotes={ctx.setNotes}
          onEditTrade={ctx.onEditTrade} onAddTrade={ctx.onAddTrade} onSessionExpired={ctx.onSessionExpired} />
      </section>
      <TradeTable trades={trades} onEditTrade={ctx.onEditTrade} />
    </div>
  );
}
